"use client";

import React, { ChangeEventHandler, ReactNode, useCallback, FC } from "react";

import { ClassNameMap } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox, { CheckboxClassKey } from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";

import { FILTER_DIVIDER } from "~/components/products-layout/constants";
import { VirtualList } from "~/components/virtual-list/component";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Nullable } from "~/types/general.types";

import {
  HandleFiltersCallback,
  AppliedFilters,
  MultiFilter as MultiFilterType,
} from "../../../../types";
import { FILTERS_CHECKBOX_SHARED_CLASSES } from "../filter-shared.constants";
import { MultiFilterContainer } from "../filter-shared.styled";

export interface MultiFilterProps {
  label: string;
  onClick: HandleFiltersCallback | undefined;
  filters: MultiFilterType[] | undefined;
  appliedFilters?: Nullable<AppliedFilters>;
  checkboxClasses?: Partial<ClassNameMap<CheckboxClassKey>>;
  checkedIcon?: ReactNode;
  className?: string;
  checked?: boolean;
}

const getEstimatedHeight = () => 48;

export const MultiFilter: FC<MultiFilterProps> = ({
  label,
  filters,
  onClick,
  appliedFilters,
  checkboxClasses,
  checkedIcon,
  className,
  checked,
}) => {
  const getKey = useCallback(
    (data: MultiFilterType, index: number) => `${data.name}_${index}`,
    [],
  );
  const renderItem = useCallback(
    (data: MultiFilterType) => {
      const { name, count } = data ?? {};
      const filterObjectKey = `${label}${FILTER_DIVIDER}${name}${FILTER_DIVIDER}${name}`;
      return (
        <MultiFilterContainer
          className={className}
          {...getTestAutomationProps("multi-filter")}
        >
          <FormControlLabel
            classes={{
              root: FILTERS_CHECKBOX_SHARED_CLASSES.formControlRoot,
              label: FILTERS_CHECKBOX_SHARED_CLASSES.formControlLabel,
            }}
            control={
              <Checkbox
                name={filterObjectKey}
                value={count}
                id={filterObjectKey}
                onChange={onClick as ChangeEventHandler<HTMLInputElement>}
                className={FILTERS_CHECKBOX_SHARED_CLASSES.checkBox}
                checked={checked ?? !!appliedFilters?.[filterObjectKey]}
                classes={checkboxClasses}
                checkedIcon={checkedIcon}
                {...getTestAutomationProps("checkbox")}
              />
            }
            label={
              <Box className={FILTERS_CHECKBOX_SHARED_CLASSES.filterItemLabel}>
                <Typography variant="body2" {...getTestAutomationProps("item-name")}>
                  {name}
                </Typography>

                <Typography variant="body2" {...getTestAutomationProps("count")}>
                  {count}
                </Typography>
              </Box>
            }
          />
        </MultiFilterContainer>
      );
    },
    [
      label,
      onClick,
      appliedFilters,
      checkboxClasses,
      checkedIcon,
      checked,
      className,
    ],
  );

  return (
    <VirtualList
      items={filters || []}
      renderItem={renderItem}
      getKey={getKey}
      estimateSize={getEstimatedHeight}
    />
  );
};
